import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Descriptions, Drawer, Form, Image, Input, Select, Table, Tag, Tooltip } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getPlatform172Detail, getProducts172 } from '../../api/external';
import type { Platform172Detail, Product172, ProductQuery172Params } from '../../api/external';

const { Option } = Select;

const Platform172Tab: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product172[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailProduct, setDetailProduct] = useState<Product172 | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [platform172Detail, setPlatform172Detail] = useState<Platform172Detail | null>(null);
  const [queryParams, setQueryParams] = useState<ProductQuery172Params>({
    page: 1,
    limit: 20,
    Age1: 0,
    Age2: 100,
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getProducts172(queryParams);
      if (response.code === 0 && response.data) {
        setProducts(response.data);
        setTotal(response.count);
      } else {
        messageApi.error(response.message || '查询失败');
      }
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || '获取172平台产品失败');
    } finally {
      setLoading(false);
    }
  }, [queryParams, messageApi]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const columns: ColumnsType<Product172> = [
    {
      title: 'ID',
      dataIndex: 'productID',
      width: 70,
    },
    {
      title: '产品名称',
      dataIndex: 'productName',
      width: 200,
      ellipsis: true,
    },
    {
      title: '主图',
      dataIndex: 'mainPic',
      width: 80,
      render: (pic) => pic ? (
        <Image src={pic} alt="" width={50} height={50} style={{ objectFit: 'cover' }} />
      ) : '-',
    },
    {
      title: '运营商',
      dataIndex: 'operator',
      width: 80,
      render: (op) => {
        const color = op === '移动' ? 'blue' : op === '联通' ? 'green' : op === '电信' ? 'orange' : 'default';
        return <Tag color={color}>{op}</Tag>;
      },
    },
    {
      title: '结算价',
      dataIndex: 'sPriceRead',
      width: 130,
      render: (val) => <span style={{ color: '#cf1322', fontWeight: 500 }}>{val || '-'}</span>,
    },
    {
      title: '返佣类型',
      dataIndex: 'backMoneyType',
      width: 100,
    },
    {
      title: '归属地',
      dataIndex: 'areaRead',
      width: 130,
      ellipsis: true,
    },
    {
      title: '禁发区域',
      dataIndex: 'disableArea',
      width: 120,
      ellipsis: { showTitle: false },
      render: (area) => area ? (
        <Tooltip title={area} placement="topLeft">
          <span style={{ color: '#ff4d4f' }}>{area}</span>
        </Tooltip>
      ) : '-',
    },
    {
      title: '年龄限制',
      dataIndex: 'age1',
      width: 90,
      render: (_, record) => `${record.age1}~${record.age2}岁`,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 180,
      ellipsis: { showTitle: false },
      render: (remark) => remark ? (
        <Tooltip title={remark} placement="topLeft">
          <span>{remark}</span>
        </Tooltip>
      ) : '-',
    },
    {
      title: '主推',
      dataIndex: 'zhutui',
      width: 60,
      render: (val) => val ? <Tag color="red">主推</Tag> : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 140,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Button type="link" size="small" onClick={async () => {
          setDetailProduct(record);
          setDetailVisible(true);
          setDetailLoading(true);
          try {
            const detail = await getPlatform172Detail(record.sn, record.userSn);
            setPlatform172Detail(detail);
          } catch (err: any) {
            messageApi.error(err?.response?.data?.message || '获取详情失败');
            setPlatform172Detail(null);
          } finally {
            setDetailLoading(false);
          }
        }}>
          详情
        </Button>
      ),
    },
  ];

  const handleSearch = (values: any) => {
    setQueryParams({
      ...queryParams,
      ...values,
      page: 1,
      limit: pageSize,
    });
  };

  const handleReset = () => {
    form.resetFields();
    setQueryParams({
      page: 1,
      limit: pageSize,
      Age1: 0,
      Age2: 100,
    });
  };

  const renderDetailField = (label: string, value: React.ReactNode) => (
    <Descriptions.Item label={label} span={2}>{value ?? '-'}</Descriptions.Item>
  );

  const renderImageField = (label: string, url: string | null | undefined) => (
    <Descriptions.Item label={label} span={2}>
      {url ? (
        <Image src={url} alt="" width={60} style={{ objectFit: 'cover' }} />
      ) : '-'}
    </Descriptions.Item>
  );

  return (
    <div style={{ padding: 0 }}>
      <div style={{ marginBottom: 12 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Form.Item name="ProductName" style={{ marginBottom: 8 }}>
            <Input placeholder="搜索产品名称" prefix={<SearchOutlined />} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="Operator" style={{ marginBottom: 8 }}>
            <Select placeholder="运营商" style={{ width: 110 }} allowClear>
              <Option value="">全部</Option>
              <Option value="移动">移动</Option>
              <Option value="联通">联通</Option>
              <Option value="电信">电信</Option>
              <Option value="广电">广电</Option>
            </Select>
          </Form.Item>
          <Form.Item name="BackMoneyType" style={{ marginBottom: 8 }}>
            <Select placeholder="返佣类型" style={{ width: 110 }} allowClear>
              <Option value="">全部</Option>
              <Option value="次月返">次月返</Option>
              <Option value="当月返">当月返</Option>
            </Select>
          </Form.Item>
          <Form.Item name="PackageType" style={{ marginBottom: 8 }}>
            <Select placeholder="套餐类型" style={{ width: 110 }} allowClear>
              <Option value="">全部</Option>
              <Option value="流量">流量</Option>
              <Option value="语音">语音</Option>
            </Select>
          </Form.Item>
          <Form.Item name="IsKuan" style={{ marginBottom: 8 }}>
            <Select placeholder="宽带" style={{ width: 100 }} allowClear>
              <Option value="">全部</Option>
              <Option value="1">宽带</Option>
              <Option value="0">非宽带</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit">搜索</Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={products}
        rowKey="productID"
        loading={loading}
        scroll={{ x: 'max-content', y: 'calc(100vh - 310px)' }}
        pagination={{
          current,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, size) => {
            setCurrent(page);
            setPageSize(size || 20);
            setQueryParams({ ...queryParams, page, limit: size || 20 });
          },
        }}
        size="middle"
      />

      <Drawer
        title={`产品详情 - ${detailProduct?.productName || ''}`}
        placement="right"
        width={720}
        onClose={() => { setDetailVisible(false); setPlatform172Detail(null); }}
        open={detailVisible}
        loading={detailLoading}
      >
        {detailProduct && (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              {renderDetailField('产品ID', detailProduct.productID)}
              {renderDetailField('产品名称', detailProduct.productName)}
              {renderImageField('主图', detailProduct.mainPic)}
              {renderDetailField('运营商', detailProduct.operator)}
              {renderDetailField('产品面值', detailProduct.price !== undefined ? `${detailProduct.price}元` : '-')}
              {renderDetailField('结算价', detailProduct.sPriceRead)}
              {renderDetailField('返佣类型', detailProduct.backMoneyType)}
              {renderDetailField('一级佣金', detailProduct.firstLevelMoney || '-')}
              {renderDetailField('二级佣金', detailProduct.blackLevelMoney || '-')}
              {renderDetailField('黄金佣金', detailProduct.goldenLevelMoney || '-')}
              {renderDetailField('商务佣金', detailProduct.busLevelMoney || '-')}
              {renderDetailField('归属地', detailProduct.areaRead || detailProduct.area || '-')}
              {renderDetailField('禁发区域', detailProduct.disableArea ? <span style={{ color: '#ff4d4f' }}>{detailProduct.disableArea}</span> : '无')}
              {renderDetailField('年龄限制', `${detailProduct.age1}~${detailProduct.age2}岁`)}
              {renderDetailField('是否需要发货', detailProduct.isNeedSend ? '需要' : '不需要')}
              {renderDetailField('规则说明', detailProduct.rule || '-')}
              {renderDetailField('关键词', detailProduct.keyWords || '-')}
            </Descriptions>

            {platform172Detail && (
              <Descriptions column={1} size="small" bordered title="HTML页面详情">
                {renderDetailField('页面标题', platform172Detail.title)}
                {renderDetailField('产品子标题', platform172Detail.subTitle)}
                {renderDetailField('月租', platform172Detail.price ? `${platform172Detail.price}元/月` : '-')}
                {renderDetailField('通用流量', platform172Detail.trafficSummary?.generalTraffic || '-')}
                {renderDetailField('定向流量', platform172Detail.trafficSummary?.directionalTraffic || '-')}
                {renderDetailField('通话时长', platform172Detail.trafficSummary?.voice || '-')}
                {renderDetailField('产品套餐', platform172Detail.productPackage || '-')}
                {renderDetailField('原套餐说明', platform172Detail.originalPlan || '-')}
                {Object.entries(platform172Detail.detailSections || {}).map(([key, value]) => (
                  <Descriptions.Item key={key} label={key} span={1}>{value || '-'}</Descriptions.Item>
                ))}
                {renderImageField('详情图', platform172Detail.detailImg)}
              </Descriptions>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};


export default Platform172Tab;
